import React, { useState } from 'react';
import { LocalizationProvider, TimeClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Controller } from 'react-hook-form';
import { Box, Typography, TextField, Button, ToggleButtonGroup, ToggleButton } from '@mui/material';
import FormErrorMessage from '../Errormessage';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const TimeClockPickerComponent = ({
  control,
  name,
  label,
  error,
  messages,
  className,
  ...rest
}) => {
  const [view, setView] = useState('hours');
  const [meridiem, setMeridiem] = useState('AM');

  dayjs.extend(utc);
  dayjs.extend(timezone);

  return (
    <Box className={className}>
      {label && <Typography variant="body1" gutterBottom>{label}</Typography>}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name={name}
          control={control}
          rules={{
            required: messages?.required,
            validate: rest.validate,
          }}
          render={({ field: { onChange, value } }) => {
            console.log('Raw value from field:', value);

            let timeValue;
            let currentMeridiem = 'AM';

            // Parse the incoming value (which could be in different formats)
            if (value) {
              // Check if it's in 12-hour format with AM/PM
              if (value.includes('AM') || value.includes('PM')) {
                timeValue = dayjs(value, 'hh:mm A');
                currentMeridiem = value.includes('AM') ? 'AM' : 'PM';
              } else {
                // If it's in 24-hour format
                timeValue = dayjs(value, 'HH:mm');
                currentMeridiem = timeValue.hour() >= 12 ? 'PM' : 'AM';
              }
              console.log('Parsed timeValue:', timeValue.format('HH:mm'));
              
              // Update meridiem state if needed
              if (meridiem !== currentMeridiem) {
                setMeridiem(currentMeridiem);
                console.log('Meridiem updated to:', currentMeridiem);
              }
            } else {
              // Default to current time
              timeValue = dayjs();
              currentMeridiem = timeValue.hour() >= 12 ? 'PM' : 'AM';
              setMeridiem(currentMeridiem);
              console.log('Defaulting timeValue to now:', timeValue.format('HH:mm'));
            }

            const handleTimeChange = (newTime) => {
              console.log('Time selected:', newTime.format('HH:mm'));

              if (newTime) {
                let hour = newTime.hour();
                console.log('Original selected hour:', hour);

                // Adjust hour based on meridiem (AM/PM)
                if (meridiem === 'PM' && hour < 12) {
                  hour += 12;
                } else if (meridiem === 'AM' && hour >= 12) {
                  hour -= 12;
                }

                const adjustedTime = dayjs(newTime).hour(hour);
                console.log('Adjusted hour with meridiem:', adjustedTime.format('HH:mm'));

                // Save in 24-hour format
                const formatted = adjustedTime.format('HH:mm');
                console.log('Formatted time to save (24-hour):', formatted);

                onChange(formatted);
              }
            };

            const handleMeridiemChange = (event, newMeridiem) => {
              if (newMeridiem !== null) {
                console.log('Meridiem changed to:', newMeridiem);
                setMeridiem(newMeridiem);

                if (value) {
                  let hour = timeValue.hour();
                  console.log('Current hour before meridiem change:', hour);

                  // Adjust hour based on meridiem change
                  if (newMeridiem === 'PM' && hour < 12) {
                    hour += 12;
                  } else if (newMeridiem === 'AM' && hour >= 12) {
                    hour -= 12;
                  }

                  const newTime = dayjs(timeValue).hour(hour);
                  
                  // Store in 24-hour format
                  const formatted = newTime.format('HH:mm');
                  console.log('Updated time after meridiem toggle (24-hour):', formatted);

                  onChange(formatted);
                }
              }
            };

            // Display in 12-hour format with AM/PM for user interface
            const displayTime = timeValue ? timeValue.format('hh:mm A') : '--:--';
            console.log('Time displayed in text field (12-hour):', displayTime);

            return (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TextField
                    value={displayTime}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                    sx={{ textAlign: 'center' }}
                  />
                </Box>

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant={view === 'hours' ? "contained" : "outlined"}
                      onClick={() => {
                        setView('hours');
                        console.log('View changed to hours');
                      }}
                    >
                      Hours
                    </Button>
                    <Button 
                      variant={view === 'minutes' ? "contained" : "outlined"}
                      onClick={() => {
                        setView('minutes');
                        console.log('View changed to minutes');
                      }}
                    >
                      Minutes
                    </Button>
                  </Box>

                  <ToggleButtonGroup
                    value={meridiem}
                    exclusive
                    onChange={handleMeridiemChange}
                    size="small"
                  >
                    <ToggleButton value="AM">AM</ToggleButton>
                    <ToggleButton value="PM">PM</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TimeClock
                  value={timeValue}
                  onChange={handleTimeChange}
                  ampm={true}
                  view={view}
                  onViewChange={(newView) => {
                    setView(newView);
                    console.log('TimeClock view changed to:', newView);
                  }}
                  {...rest}
                />
              </Box>
            );
          }}
        />
      </LocalizationProvider>
      <FormErrorMessage error={error} messages={error?.message} />
    </Box>
  );
};

export default TimeClockPickerComponent;